import {
  Badge,
  Box,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
  HISTORICO_STATUS_COLORS,
  HISTORICO_STATUS_LABELS,
  HistoricoSolicitacaoFilters,
  HistoricoSolicitacaoListResponse,
} from "@/models/auction-historico.model";
import { getAuctionHistoricoSolicitacoes } from "@/services/auctionHistorico.service";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { documentFormatter } from "@/utils/documentFormatter";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  EDGE_COLUMN_PX,
  tableCellStyles,
  tableHeaderStyles,
} from "../FinancialShared/tableStyles";
import CreditAuctionProposalsPagination from "./credit-auction-proposals-pagination";
import { formatDateTime, formatRate, toTitleCase } from "./formatters";

// whiteSpace normal libera os títulos compostos pra quebrar em 2 linhas
// (o TableContainer aplica nowrap global por padrão).
const headerStyles = {
  ...tableHeaderStyles,
  fontSize: "10px",
  whiteSpace: "normal" as const,
  lineHeight: "1.3",
};
const cellStyles = { ...tableCellStyles, fontSize: "12px" };

const brlOrDash = (value?: number | null): string =>
  value != null ? BrlCurrencyFormatter(value) : "-";

interface CreditAuctionHistoricoContentProps {
  appliedFilters: HistoricoSolicitacaoFilters;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

/**
 * Body da aba "Histórico leilão": só tabela + paginação, como a aba de
 * progresso. A fonte é o registro durável (`leilao_solicitacao_historico`) —
 * o que sobrevive ao expurgo do Redis, incluindo perdidos e NO_BID.
 */
export default function CreditAuctionHistoricoContent({
  appliedFilters,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: CreditAuctionHistoricoContentProps) {
  const backgroundStyle = useConditionalBackground();

  const { data, isLoading } = useQuery<HistoricoSolicitacaoListResponse>({
    queryKey: [
      "auction-historico-solicitacoes",
      page,
      pageSize,
      appliedFilters.searchTerm,
      appliedFilters.statuses.join(","),
      appliedFilters.codigoLote,
    ],
    queryFn: () =>
      getAuctionHistoricoSolicitacoes({
        page,
        pageSize,
        searchTerm: appliedFilters.searchTerm || undefined,
        statuses: appliedFilters.statuses.length
          ? appliedFilters.statuses
          : undefined,
        codigoLote: appliedFilters.codigoLote || undefined,
      }),
    refetchOnWindowFocus: false,
  });

  const solicitacoes = data?.data ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = data?.total_paginas ?? 1;
  const currentPage = data?.pagina_atual ?? page;

  return (
    <Box bg="white" borderRadius="xl" boxShadow="sm" px={0} py={{ base: 2, md: 4 }}>
      <CreditAuctionProposalsPagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />

      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" minH="260px">
          <Spinner size="xl" color="gray.500" />
        </Flex>
      ) : solicitacoes.length === 0 ? (
        <Flex justifyContent="center" alignItems="center" minH="260px">
          <Text color="gray.500" fontFamily="Lexend Deca">
            Nenhuma solicitação no histórico para os filtros informados.
          </Text>
        </Flex>
      ) : (
        <TableContainer borderRadius="lg" overflowX="auto">
          <Table variant="simple">
            <Thead {...backgroundStyle}>
              <Tr>
                <Th {...headerStyles} px={EDGE_COLUMN_PX} width="110px">
                  Nº SOLICITAÇÃO
                </Th>
                <Th {...headerStyles} width="120px">
                  CPF
                </Th>
                <Th {...headerStyles} width="90px">
                  STATUS
                </Th>
                <Th {...headerStyles} width="160px">
                  NOME
                </Th>
                <Th {...headerStyles} width="60px">
                  GANHOU
                </Th>
                <Th {...headerStyles} width="70px">
                  POSIÇÃO
                </Th>
                <Th {...headerStyles} width="80px">
                  VALOR SOLICITADO
                </Th>
                <Th {...headerStyles} width="70px">
                  VALOR LANCE
                </Th>
                <Th {...headerStyles} width="70px">
                  PARCELAS
                </Th>
                <Th {...headerStyles} width="100px">
                  MARGEM
                </Th>
                <Th {...headerStyles} width="70px">
                  TAXA MENSAL
                </Th>
                <Th {...headerStyles} width="70px">
                  TAXA LANCE
                </Th>
                <Th {...headerStyles} width="80px">
                  TAXA VENCEDORA
                </Th>
                <Th {...headerStyles} width="120px">
                  VALIDADE
                </Th>
                <Th {...headerStyles} px={EDGE_COLUMN_PX} width="80px">
                  FIM LEILÃO
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {solicitacoes.map((item) => (
                <Tr key={item.numeroSolicitacao} _odd={{ bg: "blackAlpha.50" }}>
                  <Td {...cellStyles} px={EDGE_COLUMN_PX} color="blue.500">
                    {item.numeroSolicitacao}
                    <Text fontSize="10px" color="gray.500">
                      {item.codigoLote ?? "-"}
                    </Text>
                  </Td>
                  <Td {...cellStyles} fontSize="10px">
                    {item.cpf ? documentFormatter(item.cpf) : "-"}
                  </Td>
                  <Td {...cellStyles}>
                    <Badge
                      colorScheme={HISTORICO_STATUS_COLORS[item.status]}
                      borderRadius="md"
                      px={2}
                      py={1}
                      fontFamily="Lexend Deca"
                      fontSize="9px"
                      fontWeight="normal"
                      whiteSpace="normal"
                      textAlign="center"
                      lineHeight="1.3"
                      display="inline-block"
                      maxW="90px"
                    >
                      {HISTORICO_STATUS_LABELS[item.status] ?? item.status}
                    </Badge>
                  </Td>
                  <Td {...cellStyles}>{toTitleCase(item.nomeTrabalhador)}</Td>
                  <Td {...cellStyles}>{item.ganhou ? "Sim" : "Não"}</Td>
                  <Td {...cellStyles}>{item.posicaoFinal ?? "-"}</Td>
                  <Td {...cellStyles}>{brlOrDash(item.valorLiberadoSolicitado)}</Td>
                  <Td {...cellStyles}>{brlOrDash(item.lanceValorLiberado)}</Td>
                  <Td {...cellStyles}>
                    {item.lanceNumeroParcelas ?? item.nroParcelasSolicitado ?? "-"}
                  </Td>
                  <Td {...cellStyles}>{brlOrDash(item.margemDisponivel)}</Td>
                  <Td {...cellStyles}>{formatRate(item.valorTaxaMensal)}</Td>
                  <Td {...cellStyles}>{formatRate(item.lanceTaxaMensal)}</Td>
                  <Td {...cellStyles}>{formatRate(item.taxaVencedora)}</Td>
                  <Td {...cellStyles}>
                    {formatDateTime(item.dataHoraValidadeSolicitacao)}
                  </Td>
                  <Td {...cellStyles} px={EDGE_COLUMN_PX}>
                    {formatDateTime(item.dataHoraFimLeilao)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <CreditAuctionProposalsPagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </Box>
  );
}
